<template>
    <div data-component="AddressForm"
         class="address-form form-subsection"
         :class="{'address-form--with-search': !searchDisabled}">
        <!-- Address search (Google Places Autocomplete) -->
        <div v-if="!searchDisabled"
             class="input-group address-form__input address-form__search-box">
            <span class="input-group-addon"><i class="fa-solid fa-search"></i></span>
            <input id="fieldAddressSearch"
                   ref="addressSearchInput"
                   type="text"
                   class="form-control input-lg address-form__input--search"
                   placeholder="Search..."
                   autocomplete="no-auto-fill"
                   data-testid="address-search"
                   :readonly="readOnly" />
        </div>

        <div class="address-form__line">
            <!-- Country -->
            <select v-model="internalAddress.country"
                    class="form-control input-lg address-form__input"
                    autocomplete="no-auto-fill"
                    data-testid="country-select"
                    :disabled="readOnly">
                <option v-for="country in countries"
                        :key="country.code"
                        :value="country">
                    {{ country.name }}
                </option>
            </select>
        </div>

        <div class="address-form__line">
            <!-- House number -->
            <input ref="houseNumberInput"
                   v-model="internalAddress.house"
                   type="text"
                   class="form-control input-lg input-house address-form__input address-form__input--house-number"
                   :placeholder="l('CUSTOMER_DETAILS.HOUSE')"
                   autocomplete="no-auto-fill"
                   data-testid="house-input"
                   :readonly="readOnly" />

            <!-- Street -->
            <input v-model="internalAddress.street"
                   type="text"
                   name="street"
                   class="form-control input-lg address-form__input"
                   :placeholder="l('CUSTOMER_DETAILS.STREET')"
                   autocomplete="no-auto-fill"
                   data-testid="street-input"
                   :readonly="readOnly" />
        </div>

        <div class="address-form__line">
            <!-- Town / City -->
            <input v-model="internalAddress.town"
                   name="town"
                   type="text"
                   class="form-control input-lg address-form__input"
                   :placeholder="l('CUSTOMER_DETAILS.TOWN')"
                   autocomplete="no-auto-fill"
                   data-testid="town-input"
                   :readonly="readOnly" />

            <!-- State (only shown for USA) -->
            <input v-if="internalAddress.country && internalAddress.country.code === 'US'"
                   v-model="internalAddress.state"
                   name="state"
                   type="text"
                   class="form-control input-lg address-form__input"
                   placeholder="State"
                   autocomplete="no-auto-fill"
                   data-testid="state-input"
                   :readonly="readOnly" />

            <!-- County (only shown for non-USA) -->
            <input v-if="internalAddress.country && internalAddress.country.code !== 'US'"
                   v-model="internalAddress.county"
                   name="county"
                   type="text"
                   class="form-control input-lg address-form__input"
                   placeholder="County"
                   autocomplete="no-auto-fill"
                   data-testid="county-input"
                   :readonly="readOnly" />
        </div>

        <div class="address-form__line">
            <!-- Postcode -->
            <input v-model="internalAddress.postcode"
                   type="text"
                   class="form-control input-lg address-form__input address-form__input--postcode"
                   :placeholder="l('CUSTOMER_DETAILS.POSTCODE')"
                   autocomplete="no-auto-fill"
                   data-testid="postcode-input"
                   :readonly="readOnly" />

            <button v-if="!readOnly"
                    class="btn btn-default btn-lg"
                    data-testid="clear-button"
                    @click.stop.prevent="clearAddress">
                {{ l('CUSTOMER_DETAILS.CLEAR_ADDRESS') }}
            </button>
        </div>
    </div>
</template>

<script>
import GoogleMapsLoader from 'google-maps';
import { container } from '@/container';
import { googleAddressComponentsToAddressData } from '@/lib/Addresses/addressFunctions';

export default {
    props: {
        address: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'clear',
    ],

    data() {
        return {
            internalAddress: this.address,
            countries: [],
            searchDisabled: false,
            autoCompleteInstance: null,
        };
    },

    computed: {
        company() {
            return container.userService.company;
        },
    },

    mounted() {
        this.countries = container.countryManager.getCountries();
        this.searchDisabled = this.readOnly;

        if (this.company.googleCloudApiKey && !this.readOnly) {
            this.initializeGooglePlaces();
        } else {
            this.searchDisabled = true;
        }
    },

    beforeUnmount() {
        if (this.autoCompleteInstance && window.google) {
            window.google.maps.event.clearListeners(this.autoCompleteInstance, 'place_changed');
        }
    },

    methods: {
        initializeGooglePlaces() {
            GoogleMapsLoader.VERSION = '3.57';
            GoogleMapsLoader.LIBRARIES = ['places'];
            GoogleMapsLoader.KEY = this.company.googleCloudApiKey;

            GoogleMapsLoader.load((google) => {
                this.setupAutoComplete(google);

                // Watch for country changes
                this.$watch('internalAddress.country', (newCountry, oldCountry) => {
                    if (!newCountry || (oldCountry && newCountry.code === oldCountry.code)) {
                        return;
                    }

                    if (this.autoCompleteInstance) {
                        google.maps.event.clearListeners(this.autoCompleteInstance, 'place_changed');
                    }
                    this.setupAutoComplete(google);
                });
            });
        },

        setupAutoComplete(google) {
            const countryCode = this.internalAddress?.country?.code || null;
            const options = this.getGoogleAutocompleteOptions(countryCode);

            this.autoCompleteInstance = new google.maps.places.Autocomplete(
                this.$refs.addressSearchInput,
                options
            );

            google.maps.event.addListener(this.autoCompleteInstance, 'place_changed', () => {
                const place = this.autoCompleteInstance.getPlace();
                if (!place || !place.address_components) {
                    return;
                }

                const addressData = googleAddressComponentsToAddressData(place.address_components);

                Object.keys(addressData).forEach((key) => {
                    this.internalAddress[key] = addressData[key];
                });

                // Ensure the 'country' in the address is a Country object not just a string.
                this.internalAddress.country = container.countryManager.findByData({
                    country: this.internalAddress.country,
                });

                // Clear the search box.
                this.$refs.addressSearchInput.value = '';
                this.$refs.houseNumberInput.focus();
            });
        },

        getGoogleAutocompleteOptions(country) {
            const options = {
                types: ['geocode'],
                fields: ['address_component'],
            };

            if (country) {
                options.componentRestrictions = {
                    country,
                };
            }

            return options;
        },

        clearAddress() {
            this.internalAddress.clear();

            this.$emit('clear');

            if (this.$refs.addressSearchInput) {
                this.$refs.addressSearchInput.value = '';
                this.$refs.addressSearchInput.focus();
            }
        },
    },
};
</script>
